import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

interface ConfigSectionProps {
  title: string
  defaultOpen?: boolean
  /** Clé localStorage stable (ex. `orion.style-sections.<widgetType>.<section>`). Omise = pas de persistance. */
  storageKey?: string
  /** Point indicateur : au moins un réglage du périmètre de la section diffère du défaut. */
  modified?: boolean
  children: React.ReactNode
}

function readStoredOpen(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey || typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (raw === 'open') return true
    if (raw === 'closed') return false
    return fallback
  } catch {
    return fallback
  }
}

export function ConfigSection({ title, defaultOpen = true, storageKey, modified = false, children }: ConfigSectionProps) {
  const [open, setOpen] = useState(() => readStoredOpen(storageKey, defaultOpen))

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev
      if (storageKey && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(storageKey, next ? 'open' : 'closed')
        } catch {
          // localStorage indisponible (mode privé, quota) : persistance en confort, pas en garantie.
        }
      }
      return next
    })
  }

  return (
    <div className="border-b border-[#E2E4ED]">
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className="flex w-full items-center justify-between py-2 text-left"
      >
        <span className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#5F6477]">{title}</span>
          {modified && <span className="h-1.5 w-1.5 rounded-full bg-[#6366F1]" aria-hidden="true" />}
        </span>
        <ChevronDown
          size={14}
          className={`text-[#9196A8] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? <div className="flex flex-col gap-1.5 pb-3">{children}</div> : null}
    </div>
  )
}
